import React, { FC, useEffect, useRef, useState } from 'react';
import * as classes from './Map.module.scss';
import { useDispatch } from 'react-redux';
import { renderToString } from 'react-dom/server';
import { Location } from '../../../interfaces/Location';
import { GoogleMapMarker } from '../../../interfaces/googleMap/GoogleMapMarker';

interface Props {
  primaryLocation: Location | null;
  markers: Array<GoogleMapMarker>;
  otherLocationsMarkerIcon: string;
  primaryLocationMarkerIcon: string;
  linkComponent?: JSX.Element;
  legendComponent?: JSX.Element;
  radiusColor?: string;
}

export const Map: FC<Props> = ({
  primaryLocation,
  markers,
  linkComponent,
  legendComponent,
  otherLocationsMarkerIcon,
  primaryLocationMarkerIcon,
  radiusColor,
}) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  useEffect(() => {
    if (markers && map) {
      markers.forEach(({ circleRadius, infoWindowContent, isPrimary, ...options }) => {
        const marker = new google.maps.Marker({
          ...options,
          icon: {
            url: isPrimary ? primaryLocationMarkerIcon : otherLocationsMarkerIcon,
            anchor: new google.maps.Point(6, 6),
          },
          map,
        });
        const infoWindow = new google.maps.InfoWindow({ content: renderToString(infoWindowContent) });
        marker.addListener('click', () => {
          infoWindow.open({
            anchor: marker,
            shouldFocus: false,
          });
        });
        if (circleRadius) {
          const fillColor = isPrimary ? '#001589' : radiusColor ? radiusColor : '#356858';
          const circle = new google.maps.Circle({
            radius: circleRadius,
            fillColor: fillColor,
            strokeWeight: 0,
            fillOpacity: 0.1,
            center: marker.getPosition(),
          });
          circle.bindTo('center', marker, 'position');
          circle.bindTo('map', marker, 'map');
          marker.addListener('map_changed', () => {
            circle.setMap(marker.getMap() as google.maps.Map);
          });
        }
      });
    }
  }, [map, markers, otherLocationsMarkerIcon, primaryLocationMarkerIcon, radiusColor]);

  useEffect(() => {
    if (ref.current && !map) {
      setMap(
        new window.google.maps.Map(ref.current, {
          center: primaryLocation,
          zoom: 10,
          disableDefaultUI: true,
          styles: [
            {
              featureType: 'administrative',
              elementType: 'geometry.fill',
              stylers: [
                {
                  visibility: 'on',
                },
                {
                  color: '#ffffff',
                },
              ],
            },
            {
              featureType: 'administrative',
              elementType: 'labels.text.fill',
              stylers: [
                {
                  gamma: '0.00',
                },
                {
                  weight: '0.01',
                },
                {
                  visibility: 'on',
                },
                {
                  color: '#8c8c8c',
                },
              ],
            },
            {
              featureType: 'administrative.neighborhood',
              elementType: 'labels.text',
              stylers: [
                {
                  visibility: 'on',
                },
              ],
            },
            {
              featureType: 'administrative.neighborhood',
              elementType: 'labels.text.fill',
              stylers: [
                {
                  color: '#898989',
                },
              ],
            },
            {
              featureType: 'administrative.neighborhood',
              elementType: 'labels.text.stroke',
              stylers: [
                {
                  color: '#ffffff',
                },
                {
                  weight: '4.00',
                },
              ],
            },
            {
              featureType: 'landscape',
              elementType: 'all',
              stylers: [
                {
                  color: '#ffffff',
                },
              ],
            },
            {
              featureType: 'landscape.man_made',
              elementType: 'geometry.fill',
              stylers: [
                {
                  visibility: 'simplified',
                },
                {
                  color: '#ffffff',
                },
              ],
            },
            {
              featureType: 'landscape.natural',
              elementType: 'geometry',
              stylers: [
                {
                  visibility: 'on',
                },
              ],
            },
            {
              featureType: 'landscape.natural',
              elementType: 'labels.text.fill',
              stylers: [
                {
                  color: '#8d8d8d',
                },
              ],
            },
            {
              featureType: 'landscape.natural.terrain',
              elementType: 'geometry.stroke',
              stylers: [
                {
                  visibility: 'on',
                },
              ],
            },
            {
              featureType: 'poi',
              elementType: 'all',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'poi.park',
              elementType: 'geometry.fill',
              stylers: [
                {
                  color: '#cef8d5',
                },
                {
                  visibility: 'on',
                },
              ],
            },
            {
              featureType: 'poi.park',
              elementType: 'labels.text.fill',
              stylers: [
                {
                  visibility: 'on',
                },
                {
                  color: '#60b36c',
                },
              ],
            },
            {
              featureType: 'poi.park',
              elementType: 'labels.text.stroke',
              stylers: [
                {
                  visibility: 'on',
                },
                {
                  color: '#ffffff',
                },
              ],
            },
            {
              featureType: 'poi.park',
              elementType: 'labels.icon',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'road',
              elementType: 'all',
              stylers: [
                {
                  saturation: '-100',
                },
                {
                  lightness: '32',
                },
                {
                  visibility: 'on',
                },
              ],
            },
            {
              featureType: 'road',
              elementType: 'geometry.fill',
              stylers: [
                {
                  color: '#f3f3f3',
                },
              ],
            },
            {
              featureType: 'road',
              elementType: 'geometry.stroke',
              stylers: [
                {
                  color: '#e1e1e1',
                },
              ],
            },
            {
              featureType: 'road',
              elementType: 'labels.text',
              stylers: [
                {
                  visibility: 'on',
                },
              ],
            },
            {
              featureType: 'road.highway',
              elementType: 'all',
              stylers: [
                {
                  visibility: 'simplified',
                },
              ],
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry',
              stylers: [
                {
                  visibility: 'on',
                },
                {
                  lightness: '63',
                },
              ],
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry.fill',
              stylers: [
                {
                  color: '#f3f3f3',
                },
              ],
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry.stroke',
              stylers: [
                {
                  color: '#e1e1e1',
                },
              ],
            },
            {
              featureType: 'road.highway',
              elementType: 'labels.text',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'road.highway',
              elementType: 'labels.icon',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'road.arterial',
              elementType: 'labels.icon',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'transit',
              elementType: 'all',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'transit.station',
              elementType: 'all',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'water',
              elementType: 'all',
              stylers: [
                {
                  visibility: 'on',
                },
                {
                  color: '#eeeeee',
                },
              ],
            },
            {
              featureType: 'water',
              elementType: 'geometry.fill',
              stylers: [
                {
                  color: '#cce4ff',
                },
              ],
            },
            {
              featureType: 'water',
              elementType: 'labels.text.fill',
              stylers: [
                {
                  visibility: 'on',
                },
                {
                  color: '#6095a5',
                },
              ],
            },
          ],
        }),
      );
    }
  }, [ref, map, primaryLocation, dispatch]);

  useEffect(() => {
    if (map && primaryLocation) {
      map.setCenter(primaryLocation);
    }
  }, [map, primaryLocation]);

  return (
    <>
      <div className={classes.mapWrapper}>
        <div ref={ref} className={classes.element} />
        {linkComponent}
      </div>
      {legendComponent}
    </>
  );
};
